import { db } from "../lib/firebase";
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc,
  query, where, limit, serverTimestamp, writeBatch, increment
} from "firebase/firestore";

// ========== HELPERS ==========
const snap = (d) => ({ id: d.id, ...d.data() });
const colSnap = async (q) => { const s = await getDocs(q); return s.docs.map(snap); };

// Normalize legacy product field names (old Firestore docs may have: name, price, cost_price, gst_rate, min_stock)
const normalizeProduct = (p) => ({
  ...p,
  brand_name: p.brand_name || p.name || "",
  selling_price: p.selling_price ?? p.price ?? 0,
  purchase_price: p.purchase_price ?? p.cost_price ?? 0,
  gst_slab: p.gst_slab ?? p.gst_rate ?? 18,
  min_stock_level: p.min_stock_level ?? p.min_stock ?? 5,
});
const getProductsNormalized = async () => {
  const raw = await colSnap(query(collection(db, "products")));
  return raw.map(normalizeProduct);
};

// ========== PRODUCTS ==========
export async function getProducts(search, category) {
  const normalized = await getProductsNormalized();
  normalized.sort((a, b) => (a.brand_name || "").localeCompare(b.brand_name || ""));
  let filtered = normalized;
  if (search) {
    const s = search.toLowerCase();
    filtered = filtered.filter(p => p.brand_name?.toLowerCase().includes(s) || p.technical_name?.toLowerCase().includes(s) || p.company_name?.toLowerCase().includes(s));
  }
  if (category && category !== "all") filtered = filtered.filter(p => p.category === category);
  return filtered;
}

export async function createProduct(data) {
  // Get next serial number without orderBy (avoids index issues)
  const allProds = await getProductsNormalized();
  const maxSerial = allProds.reduce((max, p) => Math.max(max, p.serial_number || 0), 0);
  const nextSerial = maxSerial + 1;

  const product = {
    serial_number: nextSerial,
    technical_name: data.technical_name || "",
    brand_name: data.brand_name || "",
    company_name: data.company_name || "",
    category: data.category || "pesticide",
    pack_size: data.pack_size || "",
    pack_qty: Number(data.pack_qty || 0),
    pack_unit: data.pack_unit || "ML",
    hsn_code: data.hsn_code || "",
    gst_slab: Number(data.gst_slab || 18),
    mrp: Number(data.mrp || 0),
    purchase_price: Number(data.purchase_price || 0),
    selling_price: Number(data.selling_price || 0),
    store_profit: Number(data.store_profit || 0),
    bookkeeping_markup_pct: Number(data.bookkeeping_markup_pct || 0),
    batch_no: data.batch_no || "",
    mfg_date: data.mfg_date || "",
    expiry_date: data.expiry_date || "",
    is_proxy: Boolean(data.is_proxy),
    min_stock_level: Number(data.min_stock_level || 5),
    current_stock: Number(data.current_stock || 0),
    unit: data.unit || "pcs",
    created_at: new Date().toISOString(),
  };
  const ref = await addDoc(collection(db, "products"), product);
  return { id: ref.id, ...product };
}

export async function updateProduct(id, data) {
  const updates = { ...data };
  delete updates.id;
  for (const f of ["gst_slab", "mrp", "purchase_price", "selling_price", "min_stock_level", "current_stock", "pack_qty", "bookkeeping_markup_pct", "store_profit"]) {
    if (updates[f] !== undefined) updates[f] = Number(updates[f]);
  }
  if (updates.is_proxy !== undefined) updates.is_proxy = Boolean(updates.is_proxy);
  updates.updated_at = new Date().toISOString();
  await updateDoc(doc(db, "products", id), updates);
  return { id, ...updates };
}

export async function deleteProduct(id) {
  await deleteDoc(doc(db, "products", id));
}

// ========== CUSTOMERS ==========
export async function getCustomers(search) {
  const all = await colSnap(query(collection(db, "customers")));
  all.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  let filtered = all;
  if (search) {
    const s = search.toLowerCase();
    filtered = filtered.filter(c => c.name?.toLowerCase().includes(s) || c.phone?.includes(s));
  }
  // Enrich with purchase history
  const invoices = await colSnap(query(collection(db, "invoices"), where("status", "!=", "cancelled")));
  for (const c of filtered) {
    const custInvoices = invoices.filter(inv => inv.customer?.name === c.name);
    c.total_purchases = custInvoices.length;
    c.total_amount = Math.round(custInvoices.reduce((s, inv) => s + (inv.grand_total || 0), 0) * 100) / 100;
    c.last_purchase_date = custInvoices.length > 0
      ? custInvoices.sort((a, b) => new Date(b.date) - new Date(a.date))[0]?.date || ""
      : "";
  }
  return filtered;
}

export async function createCustomer(data) {
  data.created_at = new Date().toISOString();
  const ref = await addDoc(collection(db, "customers"), data);
  return { id: ref.id, ...data };
}

export async function updateCustomer(id, data) {
  await updateDoc(doc(db, "customers", id), data);
  return { id, ...data };
}

export async function deleteCustomer(id) {
  await deleteDoc(doc(db, "customers", id));
}

export async function getCustomerActivity(name) {
  const allInvoices = await colSnap(query(collection(db, "invoices")));
  const invoices = allInvoices
    .filter(inv => inv.customer?.name === name && inv.status !== "cancelled")
    .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
    .slice(0, 5);
  const recent = invoices.map(inv => ({
    invoice_number: inv.invoice_number || "",
    date: inv.date || "",
    grand_total: Math.round((inv.grand_total || 0) * 100) / 100,
    customer_paid: inv.customer_paid ? Math.round(inv.customer_paid * 100) / 100 : null,
    payment_mode: inv.payment_mode || "cash",
    payment_status: inv.payment_status || "pending",
    item_count: (inv.items || []).length,
  }));
  // Payment pattern
  const allInv = await colSnap(
    query(collection(db, "invoices"), where("customer.name", "==", name), where("status", "!=", "cancelled"))
  );
  const pattern = {};
  for (const inv of allInv) {
    const mode = inv.payment_mode || "cash";
    if (!pattern[mode]) pattern[mode] = { count: 0, total: 0 };
    pattern[mode].count++;
    pattern[mode].total = Math.round((pattern[mode].total + (inv.grand_total || 0)) * 100) / 100;
  }
  return { recent_invoices: recent, payment_pattern: pattern };
}

// ========== SHOP SETTINGS ==========
export async function getShopSettings() {
  const docs = await colSnap(query(collection(db, "shop_settings"), limit(1)));
  return docs[0] || {};
}

export async function updateShopSettings(data) {
  const docs = await getDocs(query(collection(db, "shop_settings"), limit(1)));
  if (docs.empty) {
    await addDoc(collection(db, "shop_settings"), data);
  } else {
    await updateDoc(docs.docs[0].ref, data);
  }
  return data;
}

// ========== HSN CODES ==========
export async function getHSNCodes() {
  const all = await colSnap(query(collection(db, "hsn_codes")));
  all.sort((a, b) => (a.code || "").localeCompare(b.code || ""));
  return all;
}

// ========== STOCK CONVERSION HELPER ==========
function parsePackSize(packSize) {
  if (!packSize) return { qty: 1, unit: "PCS" };
  const str = packSize.toUpperCase().trim();
  const match = str.match(/^(\d+\.?\d*)\s*(ML|LTR|L|GM|GMS|G|KG|KGS)$/i);
  if (!match) return { qty: 1, unit: "PCS" };
  let qty = parseFloat(match[1]);
  let unit = match[2];
  if (["ML", "LTR", "L"].includes(unit)) {
    if (unit === "ML") qty = qty / 1000;
    return { qty, unit: "LTR" };
  }
  if (["GM", "GMS", "G", "KG", "KGS"].includes(unit)) {
    if (["GM", "GMS", "G"].includes(unit)) qty = qty / 1000;
    return { qty, unit: "KG" };
  }
  return { qty: 1, unit: "PCS" };
}

function convertStockToUnit(pcsCount, packSize) {
  const { qty, unit } = parsePackSize(packSize);
  if (unit === "PCS") return { value: pcsCount, unit: "PCS" };
  return { value: Math.round(pcsCount * qty * 1000) / 1000, unit };
}

// ========== INVOICES ==========
async function getNextInvoiceNumber() {
  const now = new Date();
  const year = now.getFullYear();
  const allInv = await colSnap(query(collection(db, "invoices")));
  let maxSeq = 0;
  for (const inv of allInv) {
    const num = inv.invoice_number || "";
    const m = num.match(/(\d+)$/);
    if (m) maxSeq = Math.max(maxSeq, parseInt(m[1]));
  }
  return `INV-${year}-${String(maxSeq + 1).padStart(4, "0")}`;
}

export async function getInvoices(search, invoiceType, proxyFilter) {
  const all = await colSnap(query(collection(db, "invoices")));
  all.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  let filtered = all;
  if (search) {
    const s = search.toLowerCase();
    filtered = filtered.filter(inv => inv.invoice_number?.toLowerCase().includes(s) || inv.customer?.name?.toLowerCase().includes(s));
  }
  if (invoiceType && invoiceType !== "all") filtered = filtered.filter(inv => inv.invoice_type === invoiceType);
  // Apply proxy filter
  if (proxyFilter === "non_proxy") {
    filtered = filtered.map(inv => ({
      ...inv,
      items: (inv.items || []).filter(i => !i.is_proxy),
      display_total: inv.proxy_total || inv.grand_total,
    }));
  } else if (proxyFilter === "proxy_only") {
    filtered = filtered.map(inv => ({
      ...inv,
      items: (inv.items || []).filter(i => i.is_proxy),
    }));
  }
  return filtered;
}

export async function getInvoice(id) {
  const d = await getDoc(doc(db, "invoices", id));
  if (!d.exists()) throw new Error("Invoice not found");
  return snap(d);
}

export async function getInvoiceWithDetails(id, proxyFilter) {
  let inv = await getInvoice(id);
  // Apply proxy filter
  if (proxyFilter === "non_proxy") {
    inv.items = (inv.items || []).filter(i => !i.is_proxy);
    inv.subtotal = inv.proxy_subtotal || inv.subtotal;
    inv.cgst = inv.proxy_cgst || inv.cgst;
    inv.sgst = inv.proxy_sgst || inv.sgst;
    inv.grand_total = inv.proxy_total || inv.grand_total;
  } else if (proxyFilter === "proxy_only") {
    inv.items = (inv.items || []).filter(i => i.is_proxy);
  }
  // Attach shop settings
  inv.shop_settings = await getShopSettings();
  return inv;
}

export async function createInvoice(data) {
  const invoiceNumber = await getNextInvoiceNumber();
  const items = data.items || [];
  let subtotal = 0, totalCGST = 0, totalSGST = 0, totalDiscount = 0;
  const enrichedItems = [];

  for (const item of items) {
    const prodSnap = await getDoc(doc(db, "products", item.product_id));
    if (!prodSnap.exists()) continue;
    const product = normalizeProduct({ id: prodSnap.id, ...prodSnap.data() });

    const qty = Number(item.qty || 1);
    const rate = Number(item.rate || product.selling_price);
    const discount = Number(item.discount || 0);
    const gstSlab = Number(product.gst_slab || 18);

    // Match backend: discount is flat amount
    const lineTotal = rate * qty;
    const taxable = lineTotal - discount;
    const cgst = Math.round(taxable * gstSlab / 200 * 100) / 100;
    const sgst = Math.round(taxable * gstSlab / 200 * 100) / 100;
    const lineAmount = Math.round((taxable + cgst + sgst) * 100) / 100;

    enrichedItems.push({
      product_id: item.product_id,
      product_name: product.brand_name,
      technical_name: product.technical_name || "",
      company_name: product.company_name || "",
      hsn_code: product.hsn_code || "",
      pack_size: product.pack_size || "",
      gst_slab: gstSlab,
      qty, rate, discount,
      taxable: Math.round(taxable * 100) / 100,
      cgst, sgst,
      total: lineAmount,
      is_proxy: product.is_proxy || false,
    });
    subtotal += taxable;
    totalCGST += cgst;
    totalSGST += sgst;
    totalDiscount += discount;

    // Deduct stock
    await updateDoc(doc(db, "products", item.product_id), { current_stock: (product.current_stock || 0) - qty });

    // Stock register entry
    const today = new Date(data.date || new Date().toISOString()).toISOString().split("T")[0];
    await addDoc(collection(db, "stock_register"), {
      product_id: item.product_id,
      product_name: product.brand_name,
      technical_name: product.technical_name || "",
      company_name: product.company_name || "",
      pack_size: product.pack_size || "",
      category: product.category || "",
      date: today,
      type: "sale",
      quantity: -qty,
      reference_type: "invoice",
      reference_number: invoiceNumber,
      opening_stock: product.current_stock || 0,
      closing_stock: (product.current_stock || 0) - qty,
      stock_sold: qty,
      new_stock: 0,
      remarks: `Sale: ${invoiceNumber}`,
      is_proxy: product.is_proxy || false,
      created_at: new Date().toISOString(),
    });
  }

  const grandTotal = Math.round(subtotal + totalCGST + totalSGST);
  const roundOff = Math.round((grandTotal - (subtotal + totalCGST + totalSGST)) * 100) / 100;

  // Proxy totals (non-proxy items only)
  const nonProxyItems = enrichedItems.filter(i => !i.is_proxy);
  const proxySubtotal = nonProxyItems.reduce((s, i) => s + (i.taxable || 0), 0);
  const proxyCGST = nonProxyItems.reduce((s, i) => s + (i.cgst || 0), 0);
  const proxySGST = nonProxyItems.reduce((s, i) => s + (i.sgst || 0), 0);
  const proxyTotal = Math.round(proxySubtotal + proxyCGST + proxySGST);

  const invoice = {
    invoice_number: invoiceNumber,
    invoice_type: data.invoice_type || "tax_invoice",
    date: data.date || new Date().toISOString(),
    customer: data.customer,
    items: enrichedItems,
    subtotal: Math.round(subtotal * 100) / 100,
    discount: Math.round(totalDiscount * 100) / 100,
    cgst: Math.round(totalCGST * 100) / 100,
    sgst: Math.round(totalSGST * 100) / 100,
    igst: 0,
    round_off: roundOff,
    grand_total: grandTotal,
    proxy_subtotal: Math.round(proxySubtotal * 100) / 100,
    proxy_cgst: Math.round(proxyCGST * 100) / 100,
    proxy_sgst: Math.round(proxySGST * 100) / 100,
    proxy_total: proxyTotal,
    payment_mode: data.payment_mode || "cash",
    payment_status: data.payment_status || "paid",
    status: "active",
    created_at: new Date().toISOString(),
  };
  const ref = await addDoc(collection(db, "invoices"), invoice);

  // Auto-create/update customer
  try {
    const custName = data.customer?.name;
    if (custName) {
      const existingCust = await colSnap(query(collection(db, "customers")));
      const match = existingCust.find(c => c.name === custName);
      if (match) {
        await updateDoc(doc(db, "customers", match.id), {
          total_purchases: (match.total_purchases || 0) + grandTotal,
          total_invoices: (match.total_invoices || 0) + 1,
          last_purchase_date: new Date().toISOString(),
        });
      } else {
        await addDoc(collection(db, "customers"), {
          name: custName,
          phone: data.customer?.phone || "",
          address: data.customer?.address || "",
          gstin: data.customer?.gstin || "",
          total_purchases: grandTotal,
          total_invoices: 1,
          last_purchase_date: new Date().toISOString(),
          created_at: new Date().toISOString(),
        });
      }
    }
  } catch {}

  return { id: ref.id, ...invoice };
}

export async function updateInvoice(id, data) {
  const allowed = ["payment_mode", "payment_status", "notes", "date"];
  const updates = {};
  for (const k of allowed) { if (data[k] !== undefined) updates[k] = data[k]; }
  await updateDoc(doc(db, "invoices", id), updates);
  return { id, ...updates };
}

export async function cancelInvoice(id) {
  const inv = await getInvoice(id);
  if (inv.status === "cancelled") throw new Error("Already cancelled");
  // Reverse stock
  for (const item of (inv.items || [])) {
    if (item.product_id) {
      const prodSnap = await getDoc(doc(db, "products", item.product_id));
      if (prodSnap.exists()) {
        const product = prodSnap.data();
        await updateDoc(doc(db, "products", item.product_id), { current_stock: (product.current_stock || 0) + item.qty });
      }
    }
  }
  await updateDoc(doc(db, "invoices", id), { status: "cancelled", cancelled_at: new Date().toISOString() });
  return { message: "Cancelled" };
}

export async function getInvoicePrintData(id) {
  const inv = await getInvoice(id);
  const shop = await getShopSettings();
  return { invoice: inv, shop };
}

export async function recordCustomerPayment(invoiceId, data) {
  const inv = await getInvoice(invoiceId);
  const customerPaid = Number(data.customer_paid || 0);
  const originalTotal = inv.grand_total || 0;
  const adjustment = Math.round((originalTotal - customerPaid) * 100) / 100;
  await updateDoc(doc(db, "invoices", invoiceId), {
    customer_paid: customerPaid,
    negotiation_discount: adjustment,
    display_total: customerPaid,
    payment_mode: data.payment_mode || "cash",
    payment_status: "paid",
    paid_at: new Date().toISOString(),
  });
  return { message: "Payment recorded", invoice_id: invoiceId, original_total: originalTotal, customer_paid: customerPaid, negotiation_discount: adjustment };
}

// ========== PURCHASES ==========
export async function getPurchases() {
  const all = await colSnap(query(collection(db, "purchases")));
  all.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  return all;
}

export async function createPurchase(data) {
  const items = data.items || [];
  const enrichedItems = [];
  for (const item of items) {
    const prodSnap = await getDoc(doc(db, "products", item.product_id));
    if (!prodSnap.exists()) continue;
    const product = normalizeProduct({ id: prodSnap.id, ...prodSnap.data() });
    const qty = Number(item.qty || 0);
    const rate = Number(item.rate || product.purchase_price);

    enrichedItems.push({
      product_id: item.product_id,
      product_name: product.brand_name,
      technical_name: product.technical_name || "",
      pack_size: product.pack_size || "",
      qty, rate,
      total: Math.round(rate * qty * 100) / 100,
    });

    // Add stock
    await updateDoc(doc(db, "products", item.product_id), { current_stock: (product.current_stock || 0) + qty });

    // Stock register entry
    const today = new Date(data.date || new Date().toISOString()).toISOString().split("T")[0];
    await addDoc(collection(db, "stock_register"), {
      product_id: item.product_id,
      product_name: product.brand_name,
      technical_name: product.technical_name || "",
      company_name: product.company_name || "",
      pack_size: product.pack_size || "",
      category: product.category || "",
      date: today,
      type: "purchase",
      quantity: qty,
      reference_type: "purchase",
      reference_number: data.invoice_no || "",
      supplier: data.supplier_name || "",
      purchase_invoice_no: data.invoice_no || "",
      opening_stock: product.current_stock || 0,
      closing_stock: (product.current_stock || 0) + qty,
      stock_sold: 0,
      new_stock: qty,
      remarks: `Purchase from ${data.supplier_name || "unknown"}`,
      expiry_date: data.expiry_date || "",
      is_proxy: product.is_proxy || false,
      created_at: new Date().toISOString(),
    });
  }

  const purchase = {
    date: data.date || new Date().toISOString(),
    supplier_name: data.supplier_name || "",
    supplier_location: data.supplier_location || "",
    invoice_no: data.invoice_no || "",
    items: enrichedItems,
    total: enrichedItems.reduce((s, i) => s + i.total, 0),
    payment_amount: Number(data.payment_amount || 0),
    payment_status: data.payment_status || "unpaid",
    notes: data.notes || "",
    created_at: new Date().toISOString(),
  };
  const ref = await addDoc(collection(db, "purchases"), purchase);
  return { id: ref.id, ...purchase };
}

export async function getPurchase(id) {
  const d = await getDoc(doc(db, "purchases", id));
  if (!d.exists()) throw new Error("Purchase not found");
  return snap(d);
}

export async function updatePurchase(id, data) {
  const updates = {
    supplier_name: data.supplier_name || "",
    supplier_location: data.supplier_location || "",
    invoice_no: data.invoice_no || "",
    date: data.date || "",
    notes: data.notes || "",
    payment_amount: Number(data.payment_amount || 0),
    payment_status: data.payment_status || "unpaid",
    updated_at: new Date().toISOString(),
  };
  await updateDoc(doc(db, "purchases", id), updates);
  return { id, ...updates };
}

// ========== STOCK REGISTERS ==========
export async function getStockRegisters(productId, category, proxyFilter) {
  const all = await colSnap(query(collection(db, "stock_register")));
  let filtered = all;
  if (productId) filtered = filtered.filter(e => e.product_id === productId);
  if (category && category !== "all") filtered = filtered.filter(e => e.category === category);
  if (proxyFilter === "non_proxy") filtered = filtered.filter(e => !e.is_proxy);
  else if (proxyFilter === "proxy_only") filtered = filtered.filter(e => e.is_proxy);
  filtered.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  return filtered;
}

export async function getStockRegisterPrintData(period, date, month, year) {
  const rawProducts = await getProductsNormalized();
  // Normalize legacy field names
  const products = rawProducts;
  products.sort((a, b) => (a.brand_name || "").localeCompare(b.brand_name || ""));
  const shop = await getShopSettings();

  let startDate, endDate;
  const d = date ? new Date(date) : new Date();
  if (period === "daily") {
    startDate = endDate = d.toISOString().split("T")[0];
  } else if (period === "weekly") {
    const day = d.getDay();
    const s = new Date(d); s.setDate(d.getDate() - day);
    const e = new Date(s); e.setDate(s.getDate() + 6);
    startDate = s.toISOString().split("T")[0];
    endDate = e.toISOString().split("T")[0];
  } else if (period === "monthly") {
    const y = year || d.getFullYear();
    const m = month || (d.getMonth() + 1);
    startDate = `${y}-${String(m).padStart(2, "0")}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    endDate = `${y}-${String(m).padStart(2, "0")}-${lastDay}`;
  } else if (period === "yearly") {
    const y = year || d.getFullYear();
    startDate = `${y}-01-01`;
    endDate = `${y}-12-31`;
  }

  const allRaw = await colSnap(query(collection(db, "stock_register")));
  const allRegisters = allRaw
    .filter(r => r.date >= startDate && r.date <= endDate)
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  const isMultiDay = period !== "daily";
  const prodData = [];
  let serial = 1;

  for (const product of products) {
    const entries = allRegisters.filter(r => r.product_id === product.id);
    const sold = entries.filter(e => e.type === "sale").reduce((s, e) => s + Math.abs(e.quantity || 0), 0);
    const newStock = entries.filter(e => e.type === "purchase").reduce((s, e) => s + (e.quantity || 0), 0);
    const openEntries = entries.length > 0 ? (entries[0].opening_stock || 0) : (product.current_stock || 0);
    const closing = openEntries - sold + newStock;

    const conv = convertStockToUnit(1, product.pack_size);
    const convUnit = conv.unit;
    const factor = conv.unit === "PCS" ? 1 : parsePackSize(product.pack_size).qty;

    const purchaseInvs = [...new Set(entries.filter(e => e.reference_type === "purchase").map(e => e.reference_number).filter(Boolean))];
    const suppliers = [...new Set(entries.filter(e => e.supplier).map(e => e.supplier))];

    const dateEntries = isMultiDay ? (() => {
      const dateMap = {};
      for (const e of entries) {
        if (!dateMap[e.date]) dateMap[e.date] = { date: e.date, opening_stock: e.opening_stock || 0, closing_stock: e.closing_stock || 0, stock_sold: 0, new_stock: 0, purchase_invoice_no: "", supplier: "", remarks: "" };
        if (e.type === "sale") dateMap[e.date].stock_sold += Math.abs(e.quantity || 0);
        if (e.type === "purchase") {
          dateMap[e.date].new_stock += e.quantity || 0;
          dateMap[e.date].purchase_invoice_no = e.reference_number || "";
          dateMap[e.date].supplier = e.supplier || "";
        }
        // Always update closing_stock to the LAST entry's value
        dateMap[e.date].closing_stock = e.closing_stock || dateMap[e.date].closing_stock;
        dateMap[e.date].remarks = e.remarks || dateMap[e.date].remarks;
      }
      // Recalculate: closing = opening - sold + new
      const sorted = Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
      for (let i = 0; i < sorted.length; i++) {
        if (i > 0) sorted[i].opening_stock = sorted[i - 1].closing_stock;
        sorted[i].closing_stock = sorted[i].opening_stock - sorted[i].stock_sold + sorted[i].new_stock;
      }
      return sorted;
    })() : [];

    prodData.push({
      serial_number: serial++,
      product_id: product.id,
      product_name: product.brand_name,
      technical_name: product.technical_name || "",
      company_name: product.company_name || "",
      pack_size: product.pack_size || "",
      batch_no: product.batch_no || "",
      expiry_date: product.expiry_date || "",
      category: product.category || "general",
      is_proxy: product.is_proxy || false,
      opening_stock: openEntries,
      stock_sold: sold,
      new_stock: newStock,
      closing_stock: closing,
      opening_conv: Math.round(openEntries * factor * 1000) / 1000,
      sold_conv: Math.round(sold * factor * 1000) / 1000,
      new_conv: Math.round(newStock * factor * 1000) / 1000,
      closing_conv: Math.round(closing * factor * 1000) / 1000,
      conv_unit: convUnit,
      purchase_invoice_count: purchaseInvs.length,
      suppliers: suppliers.join(", "),
      date_entries: dateEntries,
    });
  }

  return {
    shop,
    period, start_date: startDate, end_date: endDate,
    products: prodData,
  };
}

// ========== EXPENSES ==========
export async function getExpenses() {
  const all = await colSnap(query(collection(db, "expenses")));
  all.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  return all;
}

export async function createExpense(data) {
  data.amount = Number(data.amount || 0);
  data.created_at = new Date().toISOString();
  const ref = await addDoc(collection(db, "expenses"), data);
  return { id: ref.id, ...data };
}

export async function updateExpense(id, data) {
  data.amount = Number(data.amount || 0);
  await updateDoc(doc(db, "expenses", id), data);
  return { id, ...data };
}

export async function deleteExpense(id) {
  await deleteDoc(doc(db, "expenses", id));
}

// ========== DASHBOARD ==========
export async function getDashboardSummary(period) {
  const now = new Date();
  let start;
  if (period === "week") { start = new Date(now); start.setDate(now.getDate() - 7); }
  else if (period === "month") { start = new Date(now.getFullYear(), now.getMonth(), 1); }
  else if (period === "year") { start = new Date(now.getFullYear(), 0, 1); }
  else { start = new Date(now); start.setDate(now.getDate() - 30); }
  const startStr = start.toISOString();

  const invoices = await colSnap(query(collection(db, "invoices")));
  const activeInvoices = invoices.filter(i => i.status !== "cancelled" && i.date >= startStr);
  const taxInvoices = activeInvoices.filter(i => i.invoice_type === "tax_invoice");

  const totalRevenue = taxInvoices.reduce((s, i) => s + (i.grand_total || 0), 0);
  const totalInvoices = taxInvoices.length;
  const paidInvoices = taxInvoices.filter(i => i.payment_status === "paid");
  const pendingAmount = taxInvoices.filter(i => i.payment_status !== "paid").reduce((s, i) => s + (i.grand_total || 0), 0);

  const products = await getProductsNormalized();
  const customers = await colSnap(query(collection(db, "customers")));

  const lowStock = products.filter(p => (p.current_stock || 0) <= (p.min_stock_level || 5)).length;
  const nearExpiry = products.filter(p => {
    if (!p.expiry_date) return false;
    const exp = new Date(p.expiry_date);
    return exp <= new Date(Date.now() + 30 * 86400000);
  }).length;

  // Stock values
  let totalStockValueSelling = 0, totalStoreProfit = 0, totalStockCost = 0;
  for (const p of products) {
    const stock = p.current_stock || 0;
    totalStockValueSelling += stock * (p.selling_price || 0);
    totalStoreProfit += stock * (p.store_profit || 0);
    totalStockCost += stock * (p.purchase_price || 0);
  }

  // Expenses
  const expenses = await colSnap(query(collection(db, "expenses")));
  const periodExpenses = expenses.filter(e => e.date >= start.toISOString().split("T")[0]);
  const expensesTotal = periodExpenses.reduce((s, e) => s + (e.amount || 0), 0);

  // Payment modes
  const paymentModes = {};
  for (const inv of taxInvoices) {
    const mode = inv.payment_mode || "cash";
    if (!paymentModes[mode]) paymentModes[mode] = { total: 0, count: 0 };
    paymentModes[mode].total = Math.round((paymentModes[mode].total + (inv.grand_total || 0)) * 100) / 100;
    paymentModes[mode].count++;
  }

  return {
    sales: { total_revenue: Math.round(totalRevenue * 100) / 100, total_invoices: totalInvoices },
    pending_payments: Math.round(pendingAmount * 100) / 100,
    total_products: products.length,
    total_customers: customers.length,
    low_stock_count: lowStock,
    near_expiry_count: nearExpiry,
    period,
    stock_values: {
      total_stock_value_selling: Math.round(totalStockValueSelling * 100) / 100,
      total_store_profit_on_stock: Math.round(totalStoreProfit * 100) / 100,
      total_stock_cost: Math.round(totalStockCost * 100) / 100,
      total_stock_billed: Math.round((totalStockValueSelling - totalStoreProfit) * 100) / 100,
    },
    total_expenses: Math.round(expensesTotal * 100) / 100,
    payment_modes: paymentModes,
  };
}

export async function getDashboardSalesChart(period) {
  const now = new Date();
  const days = period === "week" ? 7 : 30;
  const invoices = await colSnap(query(collection(db, "invoices"), where("status", "!=", "cancelled")));

  const chart = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    const dateStr = d.toISOString().split("T")[0];
    const label = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
    const matching = invoices.filter(inv => inv.date?.startsWith(dateStr));
    chart.push({
      date: label,
      revenue: Math.round(matching.reduce((s, inv) => s + (inv.grand_total || 0), 0)),
      invoices: matching.length,
    });
  }
  return chart;
}

export async function getDashboardTopProducts() {
  const invoices = await colSnap(query(collection(db, "invoices"), where("status", "!=", "cancelled")));
  const productMap = {};
  for (const inv of invoices) {
    for (const item of (inv.items || [])) {
      const id = item.product_id || "unknown";
      if (!productMap[id]) productMap[id] = { product_id: id, product_name: item.product_name || "Unknown", total_qty: 0, total_revenue: 0 };
      productMap[id].total_qty += item.qty || 0;
      productMap[id].total_revenue += item.line_total || 0;
    }
  }
  return Object.values(productMap).sort((a, b) => b.total_revenue - a.total_revenue).slice(0, 10);
}

export async function getDashboardAlerts() {
  const products = await getProductsNormalized();
  const low_stock = [];
  const near_expiry = [];
  const out_of_stock = [];
  const now = new Date();
  const ninetyDays = new Date(Date.now() + 90 * 86400000);
  for (const p of products) {
    const stock = p.current_stock || 0;
    const minStock = p.min_stock_level || 5;
    if (stock <= 0) {
      out_of_stock.push({ type: "out_of_stock", product: p.brand_name });
    } else if (stock <= minStock) {
      low_stock.push({ type: "low_stock", product: p.brand_name, current: stock, minimum: minStock });
    }
    if (p.expiry_date) {
      const exp = new Date(p.expiry_date);
      if (exp >= now && exp <= ninetyDays) {
        near_expiry.push({ type: "near_expiry", product: p.brand_name, expiry_date: p.expiry_date });
      }
    }
  }
  return { low_stock, near_expiry, out_of_stock };
}

// ========== REPORTS ==========
export async function getSalesProfitReport(period, startDate, endDate) {
  // Compute date ranges from period if not provided (frontend only sends period)
  if (!startDate || !endDate) {
    const now = new Date();
    if (period === "today") {
      startDate = now.toISOString().split("T")[0];
      endDate = new Date(now.getTime() + 86400000).toISOString().split("T")[0];
    } else if (period === "week") {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay());
      startDate = weekStart.toISOString().split("T")[0];
      endDate = new Date(now.getTime() + 86400000).toISOString().split("T")[0];
    } else if (period === "month") {
      startDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
      endDate = new Date(now.getTime() + 86400000).toISOString().split("T")[0];
    } else if (period === "year") {
      startDate = `${now.getFullYear()}-01-01`;
      endDate = new Date(now.getTime() + 86400000).toISOString().split("T")[0];
    } else {
      startDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
      endDate = new Date(now.getTime() + 86400000).toISOString().split("T")[0];
    }
  }

  const invoices = await colSnap(query(collection(db, "invoices"), where("status", "!=", "cancelled")));
  const filtered = invoices.filter(inv => inv.date >= startDate && inv.date < endDate);

  const products = await getProductsNormalized();
  const prodMap = {};
  for (const p of products) prodMap[p.id] = p;

  // Aggregate by product
  const productAgg = {};
  for (const inv of filtered) {
    for (const item of (inv.items || [])) {
      const pid = item.product_id || "unknown";
      if (!productAgg[pid]) {
        productAgg[pid] = {
          product_id: pid,
          product_name: item.product_name || "Unknown",
          pack_size: item.pack_size || "",
          total_qty: 0, total_revenue: 0, total_discount: 0,
        };
      }
      productAgg[pid].total_qty += item.qty || 0;
      productAgg[pid].total_revenue += item.total || 0;
      productAgg[pid].total_discount += item.discount || 0;
    }
  }

  let totalRevenue = 0, totalCost = 0, totalStoreProfit = 0, totalStoreBilled = 0;
  const productsData = [];

  for (const agg of Object.values(productAgg)) {
    const product = prodMap[agg.product_id];
    const pp = product?.purchase_price || 0;
    const spPerUnit = product?.store_profit || 0;
    const selling = product?.selling_price || 0;
    const storeBilledPer = selling - spPerUnit;
    const qty = agg.total_qty;
    const cost = pp * qty;
    const revenue = agg.total_revenue;
    const grossProfit = revenue - cost;
    const storeProfit = spPerUnit * qty;
    const storeBilledTotal = storeBilledPer * qty;

    totalRevenue += revenue;
    totalCost += cost;
    totalStoreProfit += storeProfit;
    totalStoreBilled += storeBilledTotal;

    productsData.push({
      product_id: agg.product_id,
      product_name: agg.product_name,
      pack_size: agg.pack_size,
      qty_sold: qty,
      revenue: Math.round(revenue * 100) / 100,
      cost: Math.round(cost * 100) / 100,
      gross_profit: Math.round(grossProfit * 100) / 100,
      store_profit_per_unit: spPerUnit,
      store_profit_total: Math.round(storeProfit * 100) / 100,
      store_billed_per_unit: Math.round(storeBilledPer * 100) / 100,
      store_billed_total: Math.round(storeBilledTotal * 100) / 100,
      total_discount: Math.round(agg.total_discount * 100) / 100,
    });
  }

  productsData.sort((a, b) => b.revenue - a.revenue);

  const expenses = await colSnap(query(collection(db, "expenses")));
  const periodExpenses = expenses.filter(e => e.date >= startDate && e.date < endDate);
  const expensesTotal = periodExpenses.reduce((s, e) => s + (e.amount || 0), 0);

  return {
    period, start_date: startDate, end_date: endDate,
    products: productsData,
    summary: {
      total_revenue: Math.round(totalRevenue * 100) / 100,
      total_cost: Math.round(totalCost * 100) / 100,
      gross_profit: Math.round((totalRevenue - totalCost) * 100) / 100,
      total_store_profit: Math.round(totalStoreProfit * 100) / 100,
      total_store_billed: Math.round(totalStoreBilled * 100) / 100,
      total_expenses: Math.round(expensesTotal * 100) / 100,
      net_profit: Math.round((totalRevenue - totalCost - expensesTotal) * 100) / 100,
    },
  };
}

export async function getGSTReport(startDate, endDate) {
  const invoices = await colSnap(query(collection(db, "invoices"), where("status", "!=", "cancelled")));
  const filtered = invoices.filter(inv => inv.date >= startDate && inv.date <= endDate && inv.invoice_type === "tax_invoice");
  return filtered.map(inv => ({
    invoice_number: inv.invoice_number,
    date: inv.date,
    customer_name: inv.customer?.name || "",
    customer_gstin: inv.customer?.gstin || "",
    subtotal: inv.subtotal || 0,
    cgst: inv.cgst_total || 0,
    sgst: inv.sgst_total || 0,
    grand_total: inv.grand_total || 0,
    items: inv.items || [],
  }));
}

export async function getGSTHSNReport(month) {
  if (!month) {
    const now = new Date();
    month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  }
  const [y, m] = month.split("-").map(Number);
  const startDate = `${month}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const endDate = `${month}-${String(lastDay).padStart(2, "0")}`;

  const invoices = await colSnap(query(collection(db, "invoices")));
  const filtered = invoices.filter(inv =>
    inv.status !== "cancelled" &&
    inv.invoice_type === "tax_invoice" &&
    inv.date >= startDate &&
    inv.date <= (endDate + "T23:59:59")
  );

  // Group items by HSN code + GST slab
  const hsnMap = {};
  for (const inv of filtered) {
    for (const item of (inv.items || [])) {
      if (item.is_proxy) continue;
      const key = `${item.hsn_code || "N/A"}_${item.gst_slab || 0}`;
      if (!hsnMap[key]) {
        hsnMap[key] = {
          hsn_code: item.hsn_code || "N/A",
          description: item.product_name || "",
          gst_rate: item.gst_slab || 0,
          total_qty: 0, taxable_value: 0,
          cgst_amount: 0, sgst_amount: 0,
          total_value: 0, num_invoices: 0,
          invoiceSet: new Set(),
        };
      }
      hsnMap[key].total_qty += item.qty || 0;
      hsnMap[key].taxable_value += item.taxable || 0;
      hsnMap[key].cgst_amount += item.cgst || 0;
      hsnMap[key].sgst_amount += item.sgst || 0;
      hsnMap[key].total_value += item.total || 0;
      hsnMap[key].invoiceSet.add(inv.invoice_number);
    }
  }

  const hsnData = Object.values(hsnMap).map(h => ({
    hsn_code: h.hsn_code,
    description: h.description,
    uqc: "NOS",
    gst_rate: h.gst_rate,
    total_qty: h.total_qty,
    taxable_value: Math.round(h.taxable_value * 100) / 100,
    cgst_rate: h.gst_rate / 2,
    cgst_amount: Math.round(h.cgst_amount * 100) / 100,
    sgst_rate: h.gst_rate / 2,
    sgst_amount: Math.round(h.sgst_amount * 100) / 100,
    igst_rate: 0,
    igst_amount: 0,
    total_tax: Math.round((h.cgst_amount + h.sgst_amount) * 100) / 100,
    total_value: Math.round(h.total_value * 100) / 100,
    num_invoices: h.invoiceSet.size,
  }));
  hsnData.sort((a, b) => (a.hsn_code || "").localeCompare(b.hsn_code || ""));

  const grandTotals = { qty: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, total_tax: 0, total_value: 0 };
  for (const r of hsnData) {
    grandTotals.qty += r.total_qty;
    grandTotals.taxable += r.taxable_value;
    grandTotals.cgst += r.cgst_amount;
    grandTotals.sgst += r.sgst_amount;
    grandTotals.total_tax += r.total_tax;
    grandTotals.total_value += r.total_value;
  }
  for (const k of Object.keys(grandTotals)) grandTotals[k] = Math.round(grandTotals[k] * 100) / 100;

  // Invoice summary
  const allMonthInvoices = invoices.filter(inv =>
    inv.invoice_type === "tax_invoice" &&
    inv.date >= startDate &&
    inv.date <= (endDate + "T23:59:59")
  );
  const activeInvs = allMonthInvoices.filter(i => i.status !== "cancelled");
  const cancelledInvs = allMonthInvoices.filter(i => i.status === "cancelled");
  const allNums = allMonthInvoices.map(i => i.invoice_number || "").filter(Boolean).sort();

  const invSummary = {
    total_generated: allMonthInvoices.length,
    cancelled_count: cancelledInvs.length,
    active_count: activeInvs.length,
    total_revenue: Math.round(activeInvs.reduce((s, i) => s + (i.proxy_total || i.grand_total || 0), 0) * 100) / 100,
    total_cgst: Math.round(activeInvs.reduce((s, i) => s + (i.proxy_cgst || i.cgst || 0), 0) * 100) / 100,
    total_sgst: Math.round(activeInvs.reduce((s, i) => s + (i.proxy_sgst || i.sgst || 0), 0) * 100) / 100,
    first_number: allNums[0] || "",
    last_number: allNums[allNums.length - 1] || "",
  };

  const shop = await getShopSettings();

  return {
    month,
    hsn_data: hsnData,
    grand_totals: grandTotals,
    invoice_summary: invSummary,
    shop_settings: shop,
  };
}

// ========== USERS (Admin only) ==========
export async function getUsers() {
  const all = await colSnap(query(collection(db, "users")));
  all.sort((a, b) => (a.email || "").localeCompare(b.email || ""));
  return all;
}

export async function createUser(data) {
  data.created_at = new Date().toISOString();
  const ref = await addDoc(collection(db, "users"), data);
  return { id: ref.id, ...data };
}

export async function updateUser(id, data) {
  await updateDoc(doc(db, "users", id), data);
  return { id, ...data };
}

export async function deleteUser(id) {
  await deleteDoc(doc(db, "users", id));
}
